import React, { useEffect, useState } from "react";
import { HStack, Stack, Link, VStack, Flex, Avatar, Heading, Text, Box, Spinner, Center } from "@chakra-ui/react";
import { IoIosHeartEmpty, IoIosHeart } from "react-icons/io";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay } from "@chakra-ui/modal";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { userService } from "../../../services/user.service";
import Web3 from "web3";
import { nftService } from "../../../services/nft.service";
import { UserFavorite } from "../../../core/models/user-favorite";
import { shortenAddress } from "@usedapp/core";
import { generateNonce } from "../../../core/utils/securityUtils";
import { useTranslation } from "react-i18next";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";

interface Props {
  address: string | null | undefined;
  tokenAddress: string | undefined;
  tokenId: string | undefined;
}

export const FavoriteNftDetail = React.memo(({ address, tokenAddress, tokenId }: Props) => {
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [favorites, setFavorites] = useState<number>(0);
  const [users, setUsers] = useState<UserFavorite[]>();
  const [isEndOfList, setIsEndOfList] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const pageSize = 10;

  useEffect(() => {
    if (openModal) {
      const getFavoritedUsers = async () => {
        const result = await nftService.getFavoritedUsers(tokenAddress, tokenId, page, pageSize);

        if (result?.data) {
          setUsers(result.data);
        }
        setIsLoading(false);
        setIsEndOfList(!result?.data || (result.data?.length || 0) < pageSize);
      };
      getFavoritedUsers();
    }
  }, [openModal, page, tokenAddress, tokenId]);

  useEffect(() => {
    const checkFavorited = async () => {
      if (address) {
        const result = await nftService.checkFavoritedUser(tokenAddress, tokenId, address);
        if (result?.data) {
          setIsFavorite(true);
        }
      }
    };
    checkFavorited();
  }, [address, tokenAddress, tokenId]);

  useEffect(() => {
    setOpenModal(false);
    setIsLoading(true);
    setUsers(undefined);
    const getNumberFavorite = async () => {
      const result = await nftService.getNumberFavorite(tokenAddress, tokenId);
      if (result?.data !== null && result?.data !== undefined) {
        setFavorites(result.data);
      }
    };
    getNumberFavorite();
  }, [isFavorite, tokenAddress, tokenId]);

  const UserDetail = ({ avatar, name, address }: UserFavorite) => {
    return (
      <Flex
        py={3}
        px={3}
        w={"full"}
        borderColor={"gray.200"}
        borderTopWidth={"1px"}
        borderStyle={"solid"}
        cursor={"pointer"}
        onClick={() => {
          navigate(`/profile/assets/${address}`);
        }}
      >
        <HStack spacing={3}>
          <Avatar src={avatar} />
          <Box>
            <Heading fontSize={"md"}>{name}</Heading>
            <Text fontSize={"md"}>{address ? shortenAddress(address) : ""}</Text>
          </Box>
        </HStack>
      </Flex>
    );
  };

  const addFavorite = async () => {
    if (address) {
      const response = await userService.getProfileById(address);

      if (response.data) {
        const result = await nftService.addFavorite(tokenAddress, tokenId, address);
        if (result.data) {
          setIsFavorite(true);
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), address);
          const result = await nftService.addFavorite(tokenAddress, tokenId, address);
          if (result.data) {
            setIsFavorite(true);
          }
        } catch {}
      }
    }
  };

  const removeFavorite = async () => {
    if (address) {
      const response = await userService.getProfileById(address);

      if (response.data) {
        const result = await nftService.removeFavorite(tokenAddress, tokenId, address);
        if (result.data) {
          setIsFavorite(false);
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), address);
          const result = await nftService.removeFavorite(tokenAddress, tokenId, address);
          if (result.data) {
            setIsFavorite(false);
          }
        } catch {}
      }
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setIsLoading(true);
    setUsers(undefined);
  };

  const FavouriteList = () => {
    return (
      <Modal
        isOpen={openModal}
        onClose={() => {
          closeModal();
        }}
        scrollBehavior={"inside"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Favorited by")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody paddingLeft={"0"} paddingRight={"0"}>
            {isLoading ? (
              <Center alignItems="center" justifyContent="center" marginTop={10} overflow="hidden">
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
              </Center>
            ) : (
              <InfiniteScroll
                style={{
                  overflow: "hidden",
                }}
                next={() => {
                  setPage((p) => p + 1);
                }}
                hasMore={!isEndOfList}
                loader={
                  <Flex alignItems="center" justifyContent="center" marginTop={10}>
                    <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
                  </Flex>
                }
                dataLength={users?.length || 0}
              >
                <VStack spacing={0}>
                  {!!users &&
                    users.map((user, index) => (
                      <UserDetail key={index} avatar={user.avatar} name={user.name} address={user.address} />
                    ))}
                </VStack>
              </InfiniteScroll>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <>
      <Stack>
        <HStack>
          {isFavorite ? (
            <IoIosHeart onClick={() => removeFavorite()} cursor={"pointer"} />
          ) : (
            <IoIosHeartEmpty onClick={() => addFavorite()} cursor={"pointer"} />
          )}

          <Link
            fontSize={"sm"}
            fontWeight={500}
            onClick={() => {
              setOpenModal(true);
            }}
          >
            {t("favorite", { count: favorites })}
          </Link>
        </HStack>
      </Stack>

      {favorites > 0 && <FavouriteList />}
    </>
  );
});
